<script setup lang="ts">

import ColourSelector from "@/pages/Settings/AppConfigurationPage/ThemeEditor/ColourSelector.vue";
import {onMounted, ref} from "vue";
import {Json} from "@/lib/appConfiguration";
import {useGlobalStore} from "@/store";
import {convertRGBAToObject, convertRGBAObjectToString} from "@/lib/colors";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import {isYapiError, YapiError} from "@YenzaCT/sdk";
import ConfigurationSectionRow from "@/pages/Settings/AppConfigurationPage/components/ConfigurationSectionRow.vue";
import ImageUploader from "@/pages/Settings/AppConfigurationPage/components/ImageUploader.vue";

const store = useGlobalStore();

const confirmVisible = ref(false);

const variables = ref({"cta-text-color": "#000", "text-base-color": "#000", ...store.appConfiguration.cssVariables});
const ctaBackground = ref({r: 0, g: 0, b: 0, a: 1});

onMounted(() => {
  if (variables.value["cta-background"])
    ctaBackground.value = convertRGBAToObject(variables.value["cta-background"]);
});

async function save(): Promise<void> {
  confirmVisible.value = false;

  const variableUpdate = {...variables.value};
  variableUpdate["cta-background"] = convertRGBAObjectToString(ctaBackground.value);

  try {
    await store.saveAppConfiguration("cssVariables", variableUpdate as unknown as Json);
    store.showSnackbar("CSS Variables saved");
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    confirmVisible.value = false;
  }
}
</script>

<template>
  <v-card flat color="grey-lighten-4">
    <v-card-title>
      Theme Settings
    </v-card-title>
    <v-divider/>
    <v-card-text>
      <h3>Colors</h3>
      <colour-selector
        label="CTA Background Colour (RGBa)"
        v-model="ctaBackground"
        mode="rgba"
      />
      <colour-selector
        label="CTA Text Color"
        v-model="variables['cta-text-color']"
        mode="hex"
      />

      <v-divider class="my-8"/>
      <h3>Login Page</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Login page style
        </template>
        <v-select
          v-model="variables['login-page-style']"
          :items="[{title: 'Simple', value: 'simple'}, {title: 'Image', value: 'image'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row>
        <template #name>
          Login Image
        </template>
        <template #comment>
          <b>File type: </b> jpg <br/>
          <b>Recommended Size: </b> 900x800
        </template>

        <image-uploader
          configurationKey="loginImage"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Introduction Text
        </template>
        <template #comment>
          Appears in the image block
        </template>
        <div class="d-flex justify-end">
          <v-text-field
            v-model="variables['login-page-intro-text']"
            width="80px"
            density="compact"
            variant="solo"
          />
        </div>
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Content Cards</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Card Variant
        </template>
        <v-select
          v-model="variables['content-card-variant']"
          :items="[{title: 'Default', value: 'default'}, {title: 'Variant 1', value: 'variant1'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Style
        </template>
        <v-select
          v-model="variables['content-card-button-style']"
          :items="[{title: 'Filled', value: 'filled'}, {title: 'Text', value: 'text'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Full Width Button
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['content-card-full-button-width']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Font Weight
        </template>
        <v-select
          v-model="variables['content-card-heading-font-weight']"
          :items="[{title: 'Bold', value: 'font-weight-bold'},
                   {title: 'Regular', value: 'font-weight-regular'},
                   {title: 'Light', value: 'font-weight-light'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>App Header Bar</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Contain Header
        </template>
        <template #comment>
          Contained will limit width of content
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['app-header-contained']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Use Gradient
        </template>
        <template #comment>
          Will apply a gradient to the app head
        </template>
        <div class="d-flex justify-end">
          <v-text-field
            v-model="variables['app-header-gradient']"
            width="80px"
            density="compact"
            placeholder="deg"
            variant="solo"
            class="d-inline-block"
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          App Header Logo or Icon
        </template>
        <v-select
          v-model="variables['app-header-icon-type']"
          :items="[{title: 'Logo', value: 'logo'}, {title: 'Icon', value: 'icon'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Font Weight
        </template>
        <v-select
          v-model="variables['app-header-font-weight']"
          :items="[{title: 'Bold', value: 'font-weight-bold'},
                   {title: 'Regular', value: 'font-weight-regular'},
                   {title: 'Light', value: 'font-weight-light'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Menu Text Case
        </template>
        <v-select
          v-model="variables['app-header-text-case']"
          :items="[{title: 'Capitalize', value: 'text-capitalize'},
                   {title: 'Uppercase', value: 'text-uppercase'},
                   {title: 'Lowercase', value: 'text-lowercase'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Header Bar Link Color
        </template>
        <colour-selector
          v-model="variables['app-header-link-color']"
          mode="hex"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          User Menu Background Color
        </template>
        <colour-selector
          v-model="variables['app-header-user-menu-color']"
          mode="hex"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Box Shadow
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['app-header-shadow']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Round Menu Item
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['app-header-menu-item-rounded']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Show Bottom Border
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['app-header-menu-item-bottom-border']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Height
        </template>
        <v-text-field
          v-model="variables['app-header-height']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Footer</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Footer Background Color
        </template>
        <colour-selector
          v-model="variables['footer-bar-background']"
          mode="hex"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Bread Crumbs</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Bread Crumb Separator
        </template>
        <v-select
          v-model="variables['breadcrumbs-separator']"
          :items="[{title: 'Chevron', value: 'chevron'}, {title: 'Slash', value: 'slash'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Show Background
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['breadcrumbs-show-background']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Breadcrumbs Background
        </template>
        <colour-selector
          v-model="variables['breadcrumbs-background']"
          mode="hex"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Link Color
        </template>
        <colour-selector
          v-model="variables['breadcrumbs-link-color']"
          mode="hex"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Breadcrumb Y Padding
        </template>
        <v-text-field
          v-model="variables['breadcrumbs-y-padding']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Breadcrumbs Alignment
        </template>
        <v-select
          v-model="variables['breadcrumbs-alignment']"
          :items="[{title: 'Left', value: 'text-left'}, {title: 'Center', value: 'text-center'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Tabs</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Grow Tabs
        </template>
        <v-select
          v-model="variables['tab-size']"
          :items="[{title: 'Grow', value: 'grow'}, {title: 'Contain', value: 'contain'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Show Border
        </template>
        <div class="d-flex justify-end">
          <v-switch
            v-model="variables['tabs-show-border']"
            color="primary"
            density="compact"
            hide-details
          />
        </div>
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Bottom Border Color
        </template>
        <colour-selector
          v-model="variables['tabs-border-color']"
          mode="hex"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Tab Bar Height
        </template>
        <v-text-field
          v-model="variables['tabs-bar-height']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Tab Item Left Margin
        </template>
        <v-text-field
          v-model="variables['tabs-item-left-margin']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Page Header</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Text Alignment
        </template>
        <v-select
          v-model="variables['page-header-text-alignment']"
          :items="[{title: 'Left', value: 'text-left'}, {title: 'Center', value: 'text-center'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Font Weight
        </template>
        <v-select
          v-model="variables['page-header-font-weight']"
          :items="[
            {title: 'Bold', value: 'font-weight-bold'},
            {title: 'Regular', value: 'font-weight-regular'},
            {title: 'Light', value: 'font-weight-light'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Background
        </template>
        <v-select
          v-model="variables['page-header-background']"
          :items="[
            {title: 'Solid', value: 'solid'},
            {title: 'Gradient', value: 'gradient'},
            {title: 'Transparent', value: 'transparent'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Border Radius</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Root Border Radius
        </template>
        <v-text-field
          v-model="variables['border-radius-root']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Border Radius
        </template>
        <v-text-field
          v-model="variables['btn-border-radius']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Text Field Border Radius
        </template>
        <v-text-field
          v-model="variables['text-field-border-radius']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Chip Label Border Radius
        </template>
        <v-text-field
          v-model="variables['chip-label-border-radius']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>
      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Expansion Panel Border Radius
        </template>
        <v-text-field
          v-model="variables['expansion-panel-border-radius']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Buttons</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Letter Spacing
        </template>
        <v-text-field
          v-model="variables['btn-letter-spacing']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Font Weight
        </template>
        <v-text-field
          v-model="variables['btn-font-weight']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Text Transform
        </template>
        <v-text-field
          v-model="variables['btn-text-transform']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Icon Font Size
        </template>
        <v-text-field
          v-model="variables['btn-icon-font-size']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Button Border Width
        </template>
        <v-text-field
          v-model="variables['btn-outline-border-width']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <v-divider class="my-8"/>
      <h3>Typography</h3>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Heading Font Weight
        </template>
        <v-select
          v-model="variables['heading-font-weight']"
          :items="[{title: 'Black', value: '900'}, {title: 'Bold', value: '700'}, {title: 'Regular', value: '500'}, {title: 'Light', value: '300'}]"
          item-title="title"
          item-value="value"
          width="150px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Line Height
        </template>
        <v-text-field
          v-model="variables['text-line-height']"
          width="100px"
          density="compact"
          variant="solo"
          class="ml-auto"
        />
      </configuration-section-row>

      <configuration-section-row :dense="true" :show-divider="false">
        <template #name>
          Base Color
        </template>
        <colour-selector
          v-model="variables['text-base-color']"
          mode="hex"
        />
      </configuration-section-row>

    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn
        color="primary"
        @click="confirmVisible = true"
      >
        {{ $t("buttons.apply") }}
      </v-btn>
    </v-card-actions>
  </v-card>
  <confirm-dialog
    v-model="confirmVisible"
    @confirm="save"
    :title="$t('components.ThemeEditor.confirm_title')"
    :text="$t('components.ThemeEditor.confirm_text')"
  />
</template>

<style scoped>

</style>
